import Vue from 'vue';

import * as Sentry from '@sentry/browser';

import { getVm } from '@/helpers/vm-helper';
import { debugStorage } from '@/services/DebugStorage';

import BuildConfig from '../../env/build-config.json';

type WebappEnvironments = 'prod' | 'stage' | 'appdev' | 'other';
type MobileEnvironments = 'ios' | 'android';

const getSampleRate = (sentryEnvironment: WebappEnvironments | MobileEnvironments): number => {
	if (debugStorage.getState()) {
		// If user is in debug mode - we send full data.
		return 1.0;
	}
	if (sentryEnvironment === 'prod' && PLATFORM.IS_SSR) {
		return 0.01;
	}
	return 1.0;
};

const getDsn = (): string => {
	if (PLATFORM.IS_SSR) {
		return JW_CONFIG.SENTRY_DSN_SSR_CLIENT;
	}

	return JW_CONFIG.SENTRY_DSN;
};

const getRelease = (): string => {
	if (PLATFORM.IS_SSR) {
		return `jw-app:ssr-client@${BuildConfig.commitShortHash}`;
	}

	return `jw-app:spa@${BuildConfig.commitShortHash}`;
};

const getEnvironment = (): WebappEnvironments | MobileEnvironments => {
	const hostname = location.hostname;
	return hostname.includes('justwatch.com')
		? 'prod'
		: hostname.includes('dev.moviecycle.com')
		? 'appdev'
		: hostname.includes('moviecycle.com')
		? 'stage'
		: 'other';
};

let SENTRY_INITIALIZED = false;

const initSentry = async (): Promise<boolean> => {
	const dsn = getDsn();

	if (!dsn) return false;

	if (!process.client) return false;

	let environment = getEnvironment();

	try {
		const filteredIntegrations = Sentry.defaultIntegrations.filter(
			integration => !['InboundFilters', 'LinkedErrors'].includes(integration.name)
		);

		const Integrations = await import('@sentry/integrations');
		const TracingIntegrations = await (await import('@sentry/tracing')).Integrations;

		const integrations = [
			new Integrations.Vue({ Vue, attachProps: true }),
			new Integrations.CaptureConsole({ levels: ['error'] }),
			new TracingIntegrations.BrowserTracing({
				tracingOrigins: ['localhost', /^\/[^/]/],
			}),
			...filteredIntegrations,
		];

		Sentry.init({
			dsn,
			environment,
			enabled: process.env.NODE_ENV !== 'development',
			integrations,
			maxValueLength: 10000,
			release: getRelease(),
			sampleRate: getSampleRate(environment),
			tracesSampleRate: getSampleRate(environment),
		});

		SENTRY_INITIALIZED = true;
	} catch (error) {
		Sentry.close();
		throw new Error(error as any);
	}

	return true;
};

const captureMessageForSentry = async (
	name: string,
	payload: { [key: string]: any; error?: Error } = {},
	level = Sentry.Severity.Info,
	force = false
) => {
	if (!SENTRY_INITIALIZED) {
		return;
	}

	let info = {};
	let language = {};

	const state = (getVm() && getVm().$store.state) || {};
	const version = process.env.VERSION;

	let jwId = '';
	let storage: any = {};
	let languageStore: any = {};

	if ((state && debugStorage.getState()) || force) {
		jwId = state.user?.jwId;
		languageStore = state.language;

		try {
			storage = {
				user: JSON.stringify(state.user),
				filter: JSON.stringify(state.filter),
			};
		} catch (err) {}
	}

	const parseNav = {};
	if (process.client) {
		for (const property in navigator) {
			// @ts-ignore
			const value: any = navigator[property];
			if (typeof value !== 'function') {
				// @ts-ignore
				parseNav[property] = value;
			}
		}
	}

	Sentry.withScope(scope => {
		const error = payload['error'];
		const asException = level === Sentry.Severity.Error && error;
		if (asException) {
			delete payload['error'];
		}

		scope.setExtra('1. version', version);
		scope.setExtra('2. jwId', jwId);
		scope.setExtra('3. message', payload);
		scope.setExtra('4. navigator', {
			...parseNav,
			mimeTypes: undefined, // set to undefined things that we surely don't need
			plugins: undefined,
		});

		scope.setExtra('5. language store', {
			...languageStore,
			locales: undefined,
			localeMessage: undefined,
		});
		scope.setExtra('6. filter store', storage.filter);
		scope.setExtra('7. user store', storage.user);
		scope.setLevel(level);

		if (asException) {
			if (error!.message) {
				error!.message = [name, error!.message].join(' ');
			}
			Sentry.captureException(error);
		} else {
			Sentry.captureMessage(name);
		}
	});
};

export { captureMessageForSentry, initSentry, Sentry };
